<template>
  <!-- Block filters — mirrors the dataset filter UI (DatasetFilters.vue) but
       operates on the farm's blocks layer. Headers are the farm's typed
       block fields, so a Number field gives a numeric range and a
       Select/Text field gives a categorical value picker (e.g. show only
       blocks where type = Variety). Applied via setFilter in initFarmViz. -->
  <div id="farmFilters">
    <p class="mb-0 mt-4"><strong>Filters</strong></p>
    <p v-if="!filterableFields.length" class="mb-0"><small class="text-muted">Add block fields (Number, Select, Text, or Date) to filter your blocks.</small></p>
    <button
      v-else
      class="btn btn-primary btn-sm"
      @click="addFilter()"
    ><i class="fas fa-plus"></i> Add Filter</button>

    <div v-if="filters.length" class="mt-3">
      <button
        v-for="(filter, i) in filters"
        v-bind:key="i"
        class="btn btn-sm btn-secondary mr-1 mb-1"
        :class="{ active: i === selectedFilter }"
        @click="selectedFilter = i"
      ><i class="fal fa-filter"></i> Filter {{ i + 1 }}<span v-if="filter.header">: {{ filter.header }}</span></button>
    </div>

    <div v-if="filters.length && filters[selectedFilter]" class="filter">
      <button class="btn btn-danger btn-sm float-right" @click="removeFilter(selectedFilter)">Remove</button>
      <p class="mb-0"><strong>Filter {{ selectedFilter + 1 }}</strong></p>

      <!-- Header selection -->
      <div
        v-for="(field, index) in filterableFields"
        v-bind:key="index"
        class="btn btn-sm btn-primary mt-1 mr-1 clickable"
        :class="{ active: field.label === filters[selectedFilter].header }"
        @click="selectHeader(field, selectedFilter)"
      >{{ field.label }}</div>

      <!-- Numeric / date range -->
      <div
        v-if="(filters[selectedFilter].type === 'number' || filters[selectedFilter].type === 'date') && filters[selectedFilter].header"
        class="form-row ml-1 mr-1 mt-3"
      >
        <div class="col">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text">Min</span></div>
            <input v-model.number="filters[selectedFilter].min" type="number" class="form-control" @change="emitChange()" />
          </div>
        </div>
        <div class="col">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text">Max</span></div>
            <input v-model.number="filters[selectedFilter].max" type="number" class="form-control" @change="emitChange()" />
          </div>
        </div>
      </div>

      <!-- Categorical value picker -->
      <div v-if="filters[selectedFilter].type === 'category' && filters[selectedFilter].header" class="mt-2">
        <p class="mb-1"><small>Show only these values:</small></p>
        <CategoryChips
          :options="categoryOptions(selectedFilter)"
          :value="filters[selectedFilter].values || []"
          @input="changeCategory($event, selectedFilter)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CategoryChips from '../shared/CategoryChips'
const { distinctValues } = require('../../store/filterExpression')

export default {
  name: 'FarmFilters',
  components: { CategoryChips },
  data () {
    return { selectedFilter: 0 }
  },
  computed: {
    farm () {
      return this.$store.state.farm
    },
    filters () {
      return (this.farm.vizSettings && this.farm.vizSettings.filters) || []
    },
    // Block fields that make sense to filter on (typed, not hidden, not Image).
    filterableFields () {
      const fields = (this.farm && this.farm.blockFields) || []
      const ok = new Set(['Number', 'Select', 'Text', 'Date and Time'])
      return fields.filter(f => f && !f.hidden && ok.has(f.type))
    }
  },
  created () {
    // Ensure the filters array exists and is reactive.
    if (this.farm.vizSettings && !Array.isArray(this.farm.vizSettings.filters)) {
      this.$set(this.farm.vizSettings, 'filters', [])
    }
  },
  methods: {
    fieldFor (label) {
      return this.filterableFields.find(f => f.label === label)
    },
    optionsFor (field) {
      if (field && field.type === 'Select' && Array.isArray(field.options) && field.options.length) {
        return field.options.slice()
      }
      return distinctValues(this.$store.state.blocksGeoJson, field ? field.label : null)
    },
    addFilter () {
      if (!Array.isArray(this.farm.vizSettings.filters)) {
        this.$set(this.farm.vizSettings, 'filters', [])
      }
      this.farm.vizSettings.filters.push({ header: false, type: 'number', min: false, max: false, values: [] })
      this.selectedFilter = this.farm.vizSettings.filters.length - 1
      this.emitChange()
    },
    removeFilter (i) {
      this.farm.vizSettings.filters.splice(i, 1)
      this.selectedFilter = 0
      this.emitChange()
    },
    selectHeader (field, i) {
      const f = this.filters[i]
      f.header = field.label
      if (field.type === 'Number' || field.type === 'Date and Time') {
        f.type = field.type === 'Date and Time' ? 'date' : 'number'
        let mn = null
        let mx = null
        const feats = (this.$store.state.blocksGeoJson && this.$store.state.blocksGeoJson.features) || []
        feats.forEach(feat => {
          const v = Number(feat.properties && feat.properties[field.label])
          if (Number.isFinite(v)) {
            if (mn === null || v < mn) mn = v
            if (mx === null || v > mx) mx = v
          }
        })
        this.$set(f, 'min', mn === null ? 0 : mn)
        this.$set(f, 'max', mx === null ? 0 : mx)
        this.$set(f, 'values', [])
      } else {
        f.type = 'category'
        this.$set(f, 'min', false)
        this.$set(f, 'max', false)
        this.$set(f, 'values', this.optionsFor(field))
      }
      this.emitChange()
    },
    categoryOptions (i) {
      return this.optionsFor(this.fieldFor(this.filters[i].header))
    },
    changeCategory (values, i) {
      this.$set(this.filters[i], 'values', values)
      this.emitChange()
    },
    emitChange () {
      this.$emit('change')
    }
  }
}
</script>

<style scoped>
.filter {
  background-color: #eee;
  border-radius: 4px;
  margin: 10px 0;
  padding: 10px;
}
</style>
